import { useCallback, useMemo, useRef, useState } from 'react';
import { UseFormReturn, useWatch } from 'react-hook-form';
import { useQueryClient } from '@tanstack/react-query';

import {
  DesiredSettingValue,
  DesiredSettingValueKind,
} from '@crossnokaye/typescript-sdk/models/components';
import { useToaster } from '@crossnokaye/ux-common';

import { TableColumnConfig, TableRowConfig } from '@repo/sdk/models/components';

import type { SaveProgressStats } from '@/components/settings/save-progress.utils';
import type { PreviewSaveStatus } from '@/components/settings/setting-preview.types';
import type { SettingChange } from '@/components/settings/settings-save.types';
import { useSettingsSave } from '@/components/settings/use-settings-save';
import { castSettingsValue, getDesiredSettingValueKind } from '@/helpers/settings';
import { useUpdateSettings } from '@/hooks/query-hooks/settings';
import { useAtlasClient } from '@/hooks/use-atlas-client';
import { useCurrentFacility } from '@/hooks/use-current-facility';
import { listSettingsQuery } from '@/queries/settings/list-settings-query';

import {
  buildDirtyCellTargets,
  parseDirtyFieldsValues,
} from '../bulk-edit-table/use-dirty-cell-targets';
import {
  detectSettingDrift,
  extractDirtySettingIds,
  forEachDirtyCell,
  formatCellValue,
} from '../equipment-bulk-edit.helpers';
import type {
  BulkEditChangeMetadata,
  DriftedSetting,
  EquipmentBulkEditFormValues,
} from '../equipment-bulk-edit.types';

type DialogState =
  | { type: 'none' }
  | { type: 'discard'; intent: 'close' | 'reset' }
  | { type: 'save' }
  | { type: 'sync'; driftedSettings: DriftedSetting[] };

interface UseEquipmentBulkEditSaveOptions {
  columns: TableColumnConfig[];
  form: UseFormReturn<{ values: EquipmentBulkEditFormValues }>;
  rows: TableRowConfig[];
  onClose: () => Promise<void>;
}

interface UseEquipmentBulkEditSaveReturn {
  dialogState: DialogState;
  closeSaveDialog: () => CloseSaveDialogResult;
  cancelPending: () => void;
  dismissDialog: () => void;
  isDirty: boolean;
  hasPendingSave: boolean;
  saveProgressByChangeId: Record<string, PreviewSaveStatus>;
  saveComplete: boolean;
  progressStats: SaveProgressStats;
  isValidating: boolean;
  settingChanges: SettingChange[];
  dialogSettingChanges: SettingChange[];
  handleCloseAttempt: () => Promise<void>;
  handleDiscardAttempt: () => void;
  handleConfirmDiscard: () => Promise<void>;
  handleReviewSave: () => Promise<void>;
  handleSyncContinue: () => void;
  handleConfirmSave: () => Promise<void>;
}

interface CloseSaveDialogResult {
  hadSaveProgress: boolean;
}

export const useEquipmentBulkEditSave = ({
  columns,
  form,
  onClose,
  rows,
}: UseEquipmentBulkEditSaveOptions): UseEquipmentBulkEditSaveReturn => {
  const [dialogState, setDialogState] = useState<DialogState>({ type: 'none' });
  const [isValidating, setIsValidating] = useState(false);
  const displayOriginalSnapshotRef = useRef<Map<string, string>>(new Map());
  const changeIdToFormFieldRef = useRef<Map<string, { rowId: string; columnId: string }>>(
    new Map()
  );

  const parsedDirtyFields = parseDirtyFieldsValues(form.formState.dirtyFields?.values);
  const isDirty = buildDirtyCellTargets(parsedDirtyFields).length > 0;

  const queryClient = useQueryClient();
  const client = useAtlasClient();
  const { organizationId: orgId, agentId } = useCurrentFacility();
  const { mutateAsync: updateSettings } = useUpdateSettings({
    orgId,
    agentId,
    disableDefaultCallbacks: true,
  });

  const { addSuccessToast, addErrorToast } = useToaster();
  const watchedValues = useWatch<{ values: EquipmentBulkEditFormValues }>({
    name: 'values',
    control: form.control,
  }) as EquipmentBulkEditFormValues | undefined;

  const columnMap = useMemo(() => new Map(columns.map((c) => [c.columnId, c])), [columns]);

  // Build SettingChange[] reactively from dirty form state.
  // This is recalculated on every render so confirmSave always uses fresh values.
  const settingChanges = useMemo((): SettingChange[] => {
    const dirtyFields = parseDirtyFieldsValues(form.formState.dirtyFields?.values);
    const defaultValues = (form.formState.defaultValues?.values ??
      {}) as EquipmentBulkEditFormValues;

    const result: SettingChange[] = [];

    forEachDirtyCell(dirtyFields, rows, ({ rowId, columnId, row, ref }) => {
      const currentValue = watchedValues?.[rowId]?.[columnId] ?? '';
      const originalValue = defaultValues?.[rowId]?.[columnId] ?? '';
      if (currentValue === originalValue) return;

      const settingId = ref.ref!.id;
      const changeId = `${rowId}-${columnId}`;
      const valueKind = ref.ref!.valueKind;
      const kind: DesiredSettingValueKind = getDesiredSettingValueKind(valueKind);
      const castedValue = castSettingsValue(currentValue, valueKind);

      const column = columnMap.get(columnId);
      const columnLabel = column?.label ?? columnId;
      const rowName = row.name ?? rowId;

      const displayCurrent = formatCellValue(currentValue, columnId, row);
      const displayOriginal = formatCellValue(originalValue, columnId, row);
      const platformValidations = ref.ref?.validations;

      result.push({
        changeId,
        settingId,
        label: columnLabel,
        currentValue,
        displayCurrent,
        displayOriginal,
        desiredValue: {
          settingId,
          kind,
          desiredValue: { [valueKind as string]: castedValue },
        } as DesiredSettingValue,
        platformValidations,
        metadata: { rowId, rowName, columnId, columnLabel },
      });
    });

    return result;
  }, [form.formState.dirtyFields, form.formState.defaultValues, rows, watchedValues, columnMap]);

  // Freezes displayOriginal values while the save dialog is open so WebSocket
  // updates don't shift the "from" column mid-review. displayOriginalSnapshotRef
  // is intentionally absent from deps — the ref is always written before
  // dialogState transitions to 'save', so it's populated by the time this runs.
  const dialogSettingChanges = useMemo((): SettingChange[] => {
    if (dialogState.type !== 'save' || displayOriginalSnapshotRef.current.size === 0) {
      return settingChanges;
    }
    return settingChanges.map((change) => {
      const snapshotOriginal = displayOriginalSnapshotRef.current.get(change.changeId);
      if (snapshotOriginal === undefined) return change;
      return { ...change, displayOriginal: snapshotOriginal };
    });
  }, [settingChanges, dialogState.type]);

  /**
   * Called by useSettingsSave.close() after a save attempt.
   * Resets form defaults for completed cells so they are no longer dirty,
   * and fires toast notifications.
   */
  const handleSaveComplete = useCallback(
    (result: { completed: string[]; failed: string[]; cancelled: string[] }) => {
      const allCompleted =
        result.completed.length > 0 && result.failed.length === 0 && result.cancelled.length === 0;
      const hasAnyCompleted = result.completed.length > 0;

      if (allCompleted) {
        const currentValues = form.getValues('values') as EquipmentBulkEditFormValues;
        form.reset({ values: currentValues });
        addSuccessToast(`${result.completed.length} setting(s) saved successfully!`, 5000);
      } else if (hasAnyCompleted) {
        // Partial success — reset only completed cells using the snapshot
        // captured at confirmSave time (avoids stale closure on settingChanges)
        const fieldMap = changeIdToFormFieldRef.current;
        for (const changeId of result.completed) {
          const field = fieldMap.get(changeId);
          if (!field) continue;
          const { rowId, columnId } = field;
          const currentValues = form.getValues('values') as EquipmentBulkEditFormValues;
          const savedValue = currentValues[rowId]?.[columnId];
          if (savedValue === undefined) continue;
          form.resetField(`values.${rowId}.${columnId}`, { defaultValue: savedValue });
        }
        addErrorToast(
          `${result.failed.length} setting(s) failed to save. ${result.completed.length} succeeded.`,
          10000
        );
      }
    },
    [form, addSuccessToast, addErrorToast]
  );

  const settingsSave = useSettingsSave({
    changes: settingChanges,
    updateFn: updateSettings,
    onSaveComplete: handleSaveComplete,
  });

  /**
   * Closes the save dialog. Returns whether a save was attempted so the caller
   * can react (e.g. clear selection).
   */
  const closeSaveDialog = useCallback((): CloseSaveDialogResult => {
    const hadSaveProgress = Object.keys(settingsSave.saveProgressByChangeId).length > 0;
    settingsSave.close();
    setDialogState({ type: 'none' });
    displayOriginalSnapshotRef.current = new Map();
    return { hadSaveProgress };
  }, [settingsSave]);

  /** Dismisses non-save dialogs (discard, sync) without save-related side effects. */
  const dismissDialog = useCallback(() => {
    setDialogState({ type: 'none' });
  }, []);

  const handleCloseAttempt = useCallback(async () => {
    if (isDirty) {
      setDialogState({ type: 'discard', intent: 'close' });
    } else {
      await onClose();
    }
  }, [isDirty, onClose]);

  const handleDiscardAttempt = useCallback(() => {
    setDialogState({ type: 'discard', intent: 'reset' });
  }, []);

  const handleConfirmDiscard = useCallback(async () => {
    const intent = dialogState.type === 'discard' ? dialogState.intent : null;
    if (intent === 'close') {
      await onClose();
    } else {
      form.reset();
    }
    setDialogState({ type: 'none' });
  }, [dialogState, form, onClose]);

  /**
   * When "Review & Save" is clicked:
   * 1. Extract setting IDs from dirty fields
   * 2. Fetch fresh values from ListSettings
   * 3. Compare form defaults against server values
   * 4. Open sync dialog if drift detected, save dialog otherwise
   */
  const handleReviewSave = useCallback(async () => {
    if (!isDirty) {
      addErrorToast('No unsaved changes to review.', 4000);
      return;
    }

    const dirtyFields = parseDirtyFieldsValues(form.formState.dirtyFields?.values);
    const settingIds = extractDirtySettingIds(dirtyFields, rows);

    setIsValidating(true);
    try {
      const queryOptions = listSettingsQuery({
        orgId: orgId!,
        agentId: agentId!,
        settingIds,
        client,
      });
      const rawResponse = await queryClient.fetchQuery({
        ...queryOptions,
        staleTime: 0,
      });

      const freshSettings = queryOptions.select?.(rawResponse) ?? [];

      const defaultValues = (form.formState.defaultValues?.values ??
        {}) as EquipmentBulkEditFormValues;

      const drift = detectSettingDrift(dirtyFields, defaultValues, rows, freshSettings);

      if (drift.length > 0) {
        setDialogState({ type: 'sync', driftedSettings: drift });
      } else {
        displayOriginalSnapshotRef.current = new Map(
          settingChanges.map((c) => [c.changeId, c.displayOriginal])
        );
        settingsSave.open();
        setDialogState({ type: 'save' });
      }
    } catch (error) {
      displayOriginalSnapshotRef.current = new Map(
        settingChanges.map((c) => [c.changeId, c.displayOriginal])
      );
      settingsSave.open();
      setDialogState({ type: 'save' });
    } finally {
      setIsValidating(false);
    }
  }, [isDirty, form, rows, queryClient, orgId, agentId, client, settingsSave, addErrorToast]);

  /**
   * Called from SyncDialog's "Continue" button after the user has resolved
   * conflicts (selected values are already applied to the form via setValue).
   * Updates the form's defaultValues to the latest server snapshot so dirty
   * tracking is accurate, then transitions to the save dialog.
   */
  const handleSyncContinue = useCallback(() => {
    const driftedSettings = dialogState.type === 'sync' ? dialogState.driftedSettings : [];
    const currentDefaults = {
      ...((form.formState.defaultValues?.values ?? {}) as EquipmentBulkEditFormValues),
    };

    for (const ds of driftedSettings) {
      if (currentDefaults[ds.rowId]) {
        currentDefaults[ds.rowId] = {
          ...currentDefaults[ds.rowId],
          [ds.columnId]: ds.serverValue,
        };
      }
    }

    form.reset({ values: currentDefaults }, { keepValues: true, keepDirty: true });
    displayOriginalSnapshotRef.current = new Map(
      settingChanges.map((c) => [c.changeId, c.displayOriginal])
    );
    settingsSave.open();
    setDialogState({ type: 'save' });
  }, [form, dialogState, settingsSave, settingChanges]);

  const handleConfirmSave = useCallback(async () => {
    // Snapshot the changeId → form field mapping so handleSaveComplete
    // can reset cells without depending on the live settingChanges closure.
    changeIdToFormFieldRef.current = new Map(
      settingChanges.map((c) => {
        const meta = c.metadata as unknown as BulkEditChangeMetadata;
        return [c.changeId, { rowId: meta.rowId, columnId: meta.columnId }];
      })
    );
    await settingsSave.confirmSave();
  }, [settingsSave, settingChanges]);

  return {
    dialogState,
    closeSaveDialog,
    cancelPending: settingsSave.cancelPending,
    dismissDialog,
    isDirty,
    hasPendingSave: settingsSave.hasPendingSave,
    saveProgressByChangeId: settingsSave.saveProgressByChangeId,
    saveComplete: settingsSave.saveComplete,
    progressStats: settingsSave.progressStats,
    isValidating,
    settingChanges,
    dialogSettingChanges,
    handleCloseAttempt,
    handleDiscardAttempt,
    handleConfirmDiscard,
    handleReviewSave,
    handleSyncContinue,
    handleConfirmSave,
  };
};
